import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '@/services/api';
import { formatDateTime, formatNumber } from '@/services/format';
import type { UsageActionEntry, PagedResult } from '@/types';
import { UsageActionKind } from '@/types';

const KIND_LABEL: Record<UsageActionKind, string> = {
  [UsageActionKind.Used]: 'Registo',
  [UsageActionKind.Consume]: 'Débito',
  [UsageActionKind.Revert]: 'Reembolso',
  [UsageActionKind.Grant]: 'Atribuição',
};

const KIND_BADGE: Record<UsageActionKind, string> = {
  // Used = neutral, Consume = blue (committed money movement), Revert/Grant = green
  // (credits going TO the customer).
  [UsageActionKind.Used]: 'bg-gray-100 text-gray-700',
  [UsageActionKind.Consume]: 'bg-magni-blue-50 text-magni-blue-700',
  [UsageActionKind.Revert]: 'bg-magni-green-50 text-magni-green-700',
  [UsageActionKind.Grant]: 'bg-magni-green-50 text-magni-green-700',
};

export function UsageActionsPage() {
  const [data, setData] = useState<PagedResult<UsageActionEntry> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    clientId: '',
    featureToken: '',
    from: '',
    toExclusive: '',
    page: 1,
    pageSize: 50,
  });

  const load = async (override?: Partial<typeof filters>) => {
    const f = { ...filters, ...(override ?? {}) };
    setLoading(true);
    try {
      setData(await adminApi.usageActions({
        clientId: f.clientId ? Number(f.clientId) : undefined,
        featureToken: f.featureToken.trim() || undefined,
        from: f.from || undefined,
        toExclusive: f.toExclusive || undefined,
        page: f.page,
        pageSize: f.pageSize,
      }));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível carregar os eventos de utilização');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = (e: FormEvent) => { e.preventDefault(); setFilters((p) => ({ ...p, page: 1 })); load({ page: 1 }); };
  const setPage = (p: number) => { setFilters((prev) => ({ ...prev, page: p })); load({ page: p }); };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    // Wider than the standard 1280px page width because the 8-column event log doesn't fit
    // gracefully at the default; tables with this many semantic columns are the exception.
    <div className="p-8 max-w-[1600px] mx-auto">
      <h1 className="page-h1 mb-8">Eventos de utilização</h1>

      <form onSubmit={onSubmit} className="card mb-6 grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
        <label className="flex flex-col text-xs font-semibold text-gray-700">
          ID do cliente
          <input type="number" value={filters.clientId}
            onChange={(e) => setFilters((p) => ({ ...p, clientId: e.target.value }))}
            className="mt-1.5 px-3 py-2 border border-gray-200 rounded-sm text-sm" placeholder="qualquer" />
        </label>
        <label className="flex flex-col text-xs font-semibold text-gray-700">
          Token da funcionalidade
          <input value={filters.featureToken}
            onChange={(e) => setFilters((p) => ({ ...p, featureToken: e.target.value }))}
            className="mt-1.5 px-3 py-2 border border-gray-200 rounded-sm text-sm" placeholder="qualquer" />
        </label>
        <label className="flex flex-col text-xs font-semibold text-gray-700">
          Desde
          <input value={filters.from}
            onChange={(e) => setFilters((p) => ({ ...p, from: e.target.value }))}
            className="mt-1.5 px-3 py-2 border border-gray-200 rounded-sm text-sm" placeholder="aaaa-mm-dd" />
        </label>
        <label className="flex flex-col text-xs font-semibold text-gray-700">
          Até (exclusivo)
          <input value={filters.toExclusive}
            onChange={(e) => setFilters((p) => ({ ...p, toExclusive: e.target.value }))}
            className="mt-1.5 px-3 py-2 border border-gray-200 rounded-sm text-sm" placeholder="aaaa-mm-dd" />
        </label>
        <button
          type="submit"
          className="px-4 py-2 border border-magni-green-700 text-magni-green-700 hover:bg-magni-green-50 rounded-md text-sm font-semibold transition-colors"
        >Aplicar</button>
      </form>

      {loading && <p className="text-gray-500">A carregar…</p>}
      {error && <p className="text-magni-red-600">{error}</p>}
      {!loading && !error && data && (
        <>
          {/* Eight columns at default Tailwind sm font (14px) overflow the 1280px container —
              tighten horizontal padding (px-3) and let the table scroll horizontally on narrower
              viewports so Quantidade is never clipped. */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-left">
                <tr>
                  <th className="px-3 py-3 font-semibold whitespace-nowrap">Quando</th>
                  <th className="px-3 py-3 font-semibold">Tipo</th>
                  <th className="px-3 py-3 font-semibold">Cliente</th>
                  <th className="px-3 py-3 font-semibold">Utilizador</th>
                  <th className="px-3 py-3 font-semibold">Funcionalidade</th>
                  <th className="px-3 py-3 font-semibold">Ação</th>
                  <th className="px-3 py-3 font-semibold">Operador</th>
                  <th className="px-3 py-3 font-semibold text-right whitespace-nowrap">Quantidade</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((u) => (
                  <tr key={u.id} className="border-t border-gray-100">
                    <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">{formatDateTime(u.at)}</td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${KIND_BADGE[u.kind] ?? 'bg-gray-100 text-gray-700'}`}>
                        {KIND_LABEL[u.kind] ?? `Kind ${u.kind}`}
                      </span>
                      {/* Surface "reverted" state on the original Consume row so admins know it's been refunded. */}
                      {u.kind === UsageActionKind.Consume && u.revertedAt && (
                        <span className="ml-2 inline-block px-2 py-0.5 rounded text-xs font-semibold bg-gray-50 text-gray-500 line-through">revertido</span>
                      )}
                    </td>
                    <td className="px-3 py-3 font-mono">
                      {u.clientId
                        ? <Link to={`/clients/${u.clientId}`} className="text-magni-red hover:underline">{u.clientId}</Link>
                        : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-3 py-3 font-mono text-gray-700">{u.userId ?? <span className="text-gray-400">—</span>}</td>
                    <td className="px-3 py-3 font-mono text-xs text-gray-700">{u.featureToken ?? '—'}</td>
                    <td className="px-3 py-3 font-mono text-xs text-gray-700">{u.actionToken ?? <span className="text-gray-400">#{u.actionId}</span>}</td>
                    <td className="px-3 py-3 text-xs text-gray-700 break-all max-w-[180px]">{u.performedByEmail ?? <span className="text-gray-400">—</span>}</td>
                    <td className="px-3 py-3 text-right text-gray-800 whitespace-nowrap">{formatNumber(u.quantity)}</td>
                  </tr>
                ))}
                {data.items.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-500">Sem eventos.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
            <p>{formatNumber(data.total)} eventos · página {data.page} de {totalPages}</p>
            <div className="flex gap-2">
              <button disabled={data.page <= 1} onClick={() => setPage(data.page - 1)}
                className="px-3 py-1.5 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors">Anterior</button>
              <button disabled={data.page >= totalPages} onClick={() => setPage(data.page + 1)}
                className="px-3 py-1.5 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors">Seguinte</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
